import {
  Radio,
  RadioProps,
  SimpleGrid,
  Text,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core'
import { CSSProperties } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { IconType } from 'react-icons'
import classes from './Input_RadioImage.module.css'

type ImageCheckboxProps = Omit<
  RadioProps,
  'onChange' | 'name' | 'value' | 'icon' | 'ref'
> & {
  title: string
  description?: string
  icon: IconType
  value: string
  currentValue?: string
  onChange: (...e: unknown[]) => void
}

export function ImageRadio({
  title,
  description,
  className,
  icon: Icon,
  value,
  currentValue,
  onChange,
  ...others
}: ImageCheckboxProps) {
  const checked = currentValue === value
  const theme = useMantineTheme()
  const isDark = theme.colorScheme === 'dark'
  const checkedBorder = theme.fn.variant({
    variant: 'outline',
    color: theme.primaryColor,
  }).border
  const checkedBackground = theme.fn.variant({
    variant: 'light',
    color: theme.primaryColor,
  }).background
  const buttonStyle = {
    '--radio-image-padding': theme.spacing.sm,
    '--radio-image-radius': theme.radius.sm,
    '--radio-image-body-margin': theme.spacing.md,
    '--radio-image-border-color': checked
      ? checkedBorder
      : isDark
      ? theme.colors.dark[8]
      : theme.colors.gray[3],
    '--radio-image-background': checked
      ? checkedBackground
      : isDark
      ? theme.colors.dark[8]
      : theme.white,
  } as CSSProperties

  return (
    <UnstyledButton
      data-testid={`input-radio-${value}`}
      aria-label={title}
      title={title}
      onClick={() =>
        onChange({
          target: {
            value,
          },
        })
      }
      className={`${classes.button} ${className ?? ''}`}
      style={buttonStyle}
    >
      <Icon color={theme.colors.brand[5]} size={'2rem'} />

      <div className={classes.body}>
        {description && (
          <Text c="dimmed" size="xs" style={{ lineHeight: 1 }} mb={5}>
            {description}
          </Text>
        )}
        <Text fw={500} size="sm" style={{ lineHeight: 1 }}>
          {title}
        </Text>
      </div>

      <Radio value={value} checked={checked} {...others} />
    </UnstyledButton>
  )
}

type ImageRadioGroupProps = {
  items: Omit<ImageCheckboxProps, 'name' | 'onChange'>[]
  name: string
  cols?: number
}

export const InputImageRadioGroup = ({
  name,
  items,
  cols = 2,
}: ImageRadioGroupProps) => {
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...field } }) => {
        return (
          <Radio.Group {...field} ref={ref}>
            <SimpleGrid style={{ width: '100%' }} cols={{ base: 1, md: cols }}>
              {items.map(deviceType => (
                <ImageRadio
                  {...deviceType}
                  currentValue={field.value}
                  onChange={field.onChange}
                  key={deviceType.value}
                />
              ))}
            </SimpleGrid>
          </Radio.Group>
        )
      }}
    />
  )
}
